/**
 * AnalysisPage — full ticker deep-dive at /analysis
 *
 * Layout (top → bottom):
 *   1. Primer panel
 *   2. NeonHeatmap (sectors + movers)
 *   3. Saved analyses journal (Pro)
 *   4. Ticker search + curated quick-picks
 *   5. AnalysisContent (header, chart, AI thesis, trade ideas, technicals, S/R,
 *      sentiment, fundamentals)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Microscope, ArrowUpRight, GraduationCap, Loader2 } from "lucide-react";

import Header from "@/components/Header";
import { ProUpgradeModal } from "@/components/ProUpgrade";
import { fetchAnalysis } from "@/lib/api";

import { PrimerPanel } from "@/components/analysis/PrimerPanel";
import { NeonHeatmap } from "@/components/analysis/NeonHeatmap";
import { TickerPicker } from "@/components/analysis/TickerPicker";
import { AnalysisContent } from "@/components/analysis/AnalysisContent";
import {
  SaveAnalysisButton,
  SavedAnalysesPanel,
} from "@/components/analysis/SavedAnalysesPanel";
import { AnalysisSyncProvider } from "@/components/analysis/AnalysisSyncContext";

const AnalysisPage = () => {
  const [symbol, setSymbol] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get("symbol") || "NVDA").toUpperCase();
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proOpen, setProOpen] = useState(false);
  const [savedRefresh, setSavedRefresh] = useState(0);

  const load = (sym) => {
    setLoading(true);
    setError(null);
    setData(null);
    fetchAnalysis(sym)
      .then((d) => setData(d))
      .catch((err) =>
        setError(err?.response?.data?.detail || "Could not analyze that ticker"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(symbol);
  }, [symbol]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("symbol") !== symbol) {
      url.searchParams.set("symbol", symbol);
      window.history.replaceState({}, "", url.toString());
    }
  }, [symbol]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-12 px-4 sm:px-6 max-w-6xl mx-auto" data-testid="analysis-page">
        <div className="flex items-center gap-2 mb-5">
          <Microscope className="w-5 h-5 text-[#3FE5DC]" />
          <h1 className="text-[22px] font-semibold tracking-tight">Ticker Analysis</h1>
          <Link
            to="/learn"
            className="ml-auto text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-white inline-flex items-center gap-1"
            data-testid="analysis-link-learn"
          >
            <GraduationCap className="w-3.5 h-3.5" /> Glossary
            <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>

        <PrimerPanel />
        <NeonHeatmap onPick={(s) => setSymbol(s)} />
        <SavedAnalysesPanel
          refreshKey={savedRefresh}
          onPick={(s) => setSymbol(s)}
        />
        <TickerPicker value={symbol} onChange={setSymbol} />

        {loading && (
          <div className="rounded-xl border border-white/10 bg-[#070710] p-8 flex flex-col items-center gap-3 text-white/55">
            <Loader2 className="w-5 h-5 animate-spin text-[#3FE5DC]" />
            <span className="text-[12px] uppercase tracking-[0.22em]">Crunching {symbol}…</span>
          </div>
        )}
        {error && (
          <div
            className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-5 text-[12.5px] text-[#FF3B30]"
            data-testid="analysis-error"
          >
            {error}
          </div>
        )}
        {!loading && !error && data && (
          <AnalysisSyncProvider>
            <AnalysisContent
              data={data}
              onUpsellClick={() => setProOpen(true)}
              headerExtra={
                <SaveAnalysisButton
                  data={data}
                  onSaved={() => setSavedRefresh((n) => n + 1)}
                  onUpsellClick={() => setProOpen(true)}
                />
              }
            />
          </AnalysisSyncProvider>
        )}
      </main>
      <ProUpgradeModal open={proOpen} onClose={() => setProOpen(false)} />
    </div>
  );
};

export default AnalysisPage;
